import {
    useState,
    useEffect,
    useReducer,
    useContext,
    useRef,
    MutableRefObject,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../sporttia/all';
import SttLoadingBar from '../sporttia/SttLoadingBar';
import DialogComponent from '../components/dialogs/ConfirmDialog';
import SttFullPageSpinner from '../sporttia/spinners/SttFullPageSpinner';
import {
    checkObjEqual,
    getErrorMessage,
    getQueryStrings,
    secondsToFormatTime,
} from './utils';
import { SocketMessage, isErrorResponse, isObject } from '../types/common';
import { HttpVerb, MigrationApis } from '../types/api/utils';
import type { Migration } from '../types/models/Migration';
import { Game } from '../types/models/Game';
import DateBuilder from './DateBuilder';
import {
    shouldDisableAfterHandshake,
    watchdogTick,
    type WatchdogState,
} from './socketWatchdog';

/**
 * useCrudFuncs: expose context's API function as wrapper functions (Get, Post, Put, Delete) that return promises
 * autoMessages - automatically show success messages like "Created", "Saved" etc.
 * @deprecated
 */
export function useCrudFuncs(autoMessages = false) {
    const { api, showMessage, constants } = useContext(AppContext)!;
    const { t: translate } = useTranslation();

    /**
     * @deprecated
     */
    function Get<T = unknown>(
        resource: string,
        params?: Record<string, unknown>,
    ) {
        return new Promise<T>((resolve: any, reject: any) => {
            api('GET', resource, {
                params,
                success: resolve,
                error: (response: any) => {
                    if (
                        response !== constants.apiLock &&
                        isErrorResponse(response) &&
                        autoMessages
                    ) {
                        showMessage('E', response.error.msg);
                    }
                    reject(response);
                },
            });
        });
    }

    /**
     * @deprecated
     */
    function Post<T = unknown>(
        resource: string,
        params?: Record<string, unknown>,
        confirmation = false,
    ) {
        return new Promise<T>((resolve: any, reject: any) => {
            api('POST', resource, {
                confirmation,
                params,
                success: (response: any) => {
                    if (autoMessages) {
                        showMessage('S', translate('Created'));
                    }
                    resolve(response);
                },
                error: (response: any) => {
                    if (
                        response !== constants.apiLock &&
                        isErrorResponse(response) &&
                        autoMessages
                    ) {
                        showMessage('E', response.error.msg);
                    }
                    reject(response);
                },
            });
        });
    }

    /**
     * @deprecated
     */
    function Put<T = unknown>(
        resource: string,
        params?: Record<string, unknown>,
        confirmation = false,
    ) {
        return new Promise<T>((resolve: any, reject: any) => {
            api('PUT', resource, {
                confirmation,
                params,
                success: (response: any) => {
                    if (autoMessages) {
                        showMessage('S', translate('Saved'));
                    }
                    resolve(response);
                },
                error: (response: any) => {
                    if (
                        response !== constants.apiLock &&
                        isErrorResponse(response) &&
                        autoMessages
                    ) {
                        showMessage('E', response.error.msg);
                    }
                    reject(response);
                },
            });
        });
    }

    /**
     * @deprecated
     */
    function Delete<T = unknown>(
        resource: string,
        params?: Record<string, unknown>,
        confirmation = true,
    ) {
        return new Promise<T>((resolve: any, reject: any) => {
            api('DELETE', resource, {
                confirmation,
                params,
                success: (response: any) => {
                    if (autoMessages) {
                        showMessage('S', translate('Deleted'));
                    }
                    resolve(response);
                },
                error: (response: any) => {
                    if (
                        response !== constants.apiLock &&
                        isErrorResponse(response) &&
                        autoMessages
                    ) {
                        showMessage('E', response.error.msg);
                    }
                    reject(response);
                },
            });
        });
    }

    return [Get, Post, Put, Delete] as const;
}

/**
 * Note that this component handles the value as int values instead of bool even if bools are passed to it, for ex. calling useToggle(true)
 * This is because the API always accepts trash:1/0 but not always accepts trash:true/false; and also because 1 and 0 are truthy and falsy in Javascript anyway.
 */
export function useToggle(
    callback: (() => void) | undefined,
    initialState: boolean = false,
) {
    function reducer(state: { ready: boolean; value: number }, action: number) {
        if (action === +!state.value) {
            return { value: action, ready: true };
        }
        return state;
    }

    const [state, setState] = useReducer(reducer, {
        ready: false,
        value: +(initialState || false),
    });

    useEffect(() => {
        if (state.ready && typeof callback === 'function') {
            callback();
        }
    }, [state.value]);

    function toggleState(force?: boolean) {
        if (force !== undefined) {
            setState(+force);
        } else {
            setState(+!!state.value);
        }
    }

    return [Boolean(state.value), toggleState] as const;
}

/**
 * Hook to automate tuple updating in objects like {name1: value1, name2: value2}
 */
export function useTuples<T extends Record<string, unknown>>(initialObject: T) {
    const [tuples, setTuples] = useState<T>(initialObject);

    function setTuple({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (name) {
            setTuples({ ...tuples, [name]: value } as T);
        }
    }

    return [tuples, setTuple] as const;
}

/**
 * Hook to automate the state of an object that has a direct representation in the back end.
 * @deprecated
 */
export function useCrud<T extends object>(
    initialState: T = {} as T,
    autoMessages = true,
) {
    const [item, setItem] = useState<T>(initialState);

    const [Get, Post, Put, Delete] = useCrudFuncs(autoMessages);

    function setProperty({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (name) {
            // Use functional update to avoid stale closure issues
            setItem((prevItem) => ({ ...prevItem, [name]: value }) as T);
        }
    }

    return [item, setItem, setProperty, Get, Post, Put, Delete] as const;
}

/**
 * Hook para consulta la API de Sporttia.
 * @param knownMigrationApis Endpoint que estan migrados a V7.
 * @param additionalOptions Opciones adicionales.
 * @returns
 */
export function useAPI(
    knownMigrationApis: MigrationApis | null = null,
    additionalOptions?: {
        version?: string;
        [key: string]: unknown;
    },
) {
    const [apiLocks, setApiLocks] = useState<string[]>([]);
    const [migrationEndpoints, setMigrationEndpoints] =
        useState(knownMigrationApis); // null required for proper initialization
    const apiCache: Record<string, unknown> = {};
    const { apiEnv } = window.runtimeConfig;

    /**
     * Formatea un array de endpoints de la migración desde el formato [{id, method, endpoint, active, createdAt, updatedAt}]
     * al formato {endpoint1: [method1, method2, method3], endpoint2: [method1, method2, method3]}. Básicamente un objeto en
     * el que la clave primaria es el path/endpoint al que se le asigna un array con los métodos que estan disponibles para
     * ese endpoint.
     * @param {MigrationEndpoints} endpoints - El objeto que contiene la información de las migraciones de EPS.
     * @returns Objeto formateado con las claves de endpoint y valores de método.
     */
    function formatMigrationsEps(endpoints: Migration[]) {
        return endpoints.reduce(
            (formatedMigrations: Record<string, string[]>, endpoint) => {
                const { endpoint: e, method } = endpoint;
                const parsedEndpoint = e
                    .replace(/\{\w+\}/gi, '{param}')
                    .replace('/v7', '');

                const newFormatedMigrations = { ...formatedMigrations };

                newFormatedMigrations[parsedEndpoint] =
                    newFormatedMigrations[parsedEndpoint] || [];
                newFormatedMigrations[parsedEndpoint].push(method);

                return newFormatedMigrations;
            },
            {},
        );
    }

    /**
     * Función para setear los endpoints desde fuera del hook.
     * @param paths Array con la respuesta de la api de GET /migration-endpoints.
     */
    function setMigrationApis(paths: Migration[]) {
        const formatedMigrationEps = formatMigrationsEps(paths);

        setMigrationEndpoints(formatedMigrationEps);
    }

    /**
     * Obtiene la versión del endpoint dado los endpoints que tenemos migrados a V7.
     * @param method Método HTTP.
     * @param resource Path a consultar.
     * @returns
     */
    function getEPVersion(method: HttpVerb, resource: string) {
        // If this resource's version has already been determined, simply return it.
        if (apiCache[`${method}|${resource}`]) {
            return apiCache[`${method}|${resource}`]!;
        }

        // Se setea por defecto la versión a V6 a no ser que se haya pasado como campo adicional.
        let version = (additionalOptions && additionalOptions.version) || 'v8';

        // Check against migration apis in the shape ~/stuff.stuff (like 1234.jpg or ticket.pdf)
        if (migrationEndpoints && Object.keys(migrationEndpoints).length > 0) {
            // Se pasa un endpoint que se consulta al formato generico. Un ejemplo:
            // - /cities/1234 ---> /cities/{param}
            // - /cities?page=12&&rows=20 ---> /cities
            const generalization = resource
                .replace(/\/[\d]+(\.[\w\d]{3,4})?/gi, '/{param}$1')
                .replace(/\/[\d]+/, '/{param}')
                .replace(/(\?.*)|(#.*)/g, '');

            // Se busca si la el path formateado existe en el array de "migrationEndpoints".
            const endPointName = Object.keys(migrationEndpoints).includes(
                generalization,
            )
                ? generalization
                : false;

            // Si antes se ha encontrado un endpoint ya migrado entonces se comprueba que el método
            // http con el consultamos también existe y se devuelve la nueva versión (v7).
            if (endPointName) {
                const endPoint = migrationEndpoints[endPointName];

                if (endPoint && endPoint.includes(method)) {
                    version = 'v7';
                }
            }
        }

        // Aquí básicamente nos saltamos la comprobación de si un path existe en el objeto de endpoints
        // migrados ya que si el endpoint que se pide es "/migration-endpoints" solo existe en v7 y si
        // no lo tenemos activado desde el front no vamos a poder entrar en la vista de cargarlos porque
        // no existe en v6. En resument, este endpoint es unico y propio de v7.
        const result = resource.includes('/migration-endpoints')
            ? 'v7'
            : version;

        // Cache for repeated use (mutation because React shenanigans)
        apiCache[`${method}|${resource}`] = result;

        return result;
        // return 'v7';
    }

    /**
    * General request method that pretty much does everything on its own
        Returns a Promise that resolves to:
            The response JSON for 200 OK
            {error : {msg: "<error_msg>", log: "<log_id>"}} for 401, 403, 404 and 500
            {errors: [{key: ["<error_msg>"...]}...], log: "<log_id>"}} for 400
    * @param method Metodo HTTP.
    * @param resource Endpoint a consultar.
    * @param params Parámetros de consulta.
    * @returns
    */
    function request<T>(
        method: HttpVerb,
        resource: string,
        params?: Record<string, unknown>,
        versionOverride?: string,
    ) {
        const apiIndex = method + resource + JSON.stringify(params);

        if (method !== 'GET') {
            if (apiLocks.includes(apiIndex)) {
                // locked API

                return Promise.reject('LOCK'); // Aquí lamentablemente no es fiable acceder a cxt.constants
            }

            setApiLocks(apiLocks.concat([apiIndex]));
        }

        const METHOD = method.toUpperCase() as HttpVerb;

        const version = versionOverride || getEPVersion(METHOD, resource);

        const headers = {
            Accept: 'application/json',
            'Content-Type':
                version === 'v6'
                    ? 'application/x-www-form-urlencoded'
                    : 'application/json',
        };

        let myResource = resource;

        // concat params into the querystring, but only for params which aren't undefined or null (to mimic previous behavior)
        if (METHOD === 'GET' && params) {
            if (Object.keys(params).length > 0) {
                // extract non-null non-undefined params from the provided ones
                const myParams = Object.keys(params).reduce(
                    (result, param) =>
                        params[param] !== undefined && params[param] !== null
                            ? {
                                  ...result,
                                  [param]: params[param],
                              }
                            : result,
                    {},
                );

                // contact the querystring of the resulting params
                myResource += Object.keys(myParams).reduce(
                    (result, param, i) =>
                        `${result + (i > 0 ? '&' : '?') + param}=${
                            params[param]
                        }`,
                    '',
                );
            }
        }

        // TODO: QUITAR ESTO CUANDO SE SUBA LA NUEVA APP
        const uri = `${apiEnv.url}/${resource !== '/timetable' ? version : 'v7'}${
            myResource.charAt(0) === '/' ? myResource : `/${myResource}`
        }`;

        return new Promise<T>((resolve: any, reject: any) => {
            // For POST, PUT and DELETE: append params object as request body
            fetch(uri, {
                headers,
                method,
                cache: 'no-cache',
                redirect: 'follow',
                credentials: 'include',
                ...(METHOD === 'POST' || METHOD === 'PUT' || METHOD === 'DELETE'
                    ? {
                          // Add params to body in these cases
                          body: JSON.stringify(params),
                      }
                    : {}),
            })
                .then((response: any) => {
                    if (response.status === 204) {
                        resolve('' as T);
                    } else if (
                        response.status === 200 ||
                        response.status === 201
                    ) {
                        try {
                            // One of these can still be a logical error; for example: wrong password, inssuficent funds, etc... and also every non-500 error from v6 lol
                            // response.json() does not pre-check if the body text is empty. Added this to filter the content of the body and avoid runtime errors.
                            response.text().then((text: any) => {
                                if (text.length > 0) {
                                    const tentativeResponse = JSON.parse(
                                        text,
                                    ) as T;

                                    // Si la respuesta es 200 o 201, pero tiene la forma {error: {msg}}, lo tratamos como un error y rechazamos
                                    if (isErrorResponse(tentativeResponse)) {
                                        reject(tentativeResponse);
                                    } else {
                                        resolve(tentativeResponse);
                                    }
                                } else {
                                    resolve('' as T); // Resolve to success with empty value string for those endpoints that dont't need to return anything
                                }
                            });
                        } catch (error) {
                            reject({
                                error: {
                                    msg: error,
                                },
                            });
                        }
                    } else if (response.status === 400) {
                        response.json().then((result: any) => {
                            reject({
                                ...result,
                                log: response.headers.get('X-Request-Id'),
                            });
                        });
                    } else if (response.status === 503) {
                        reject({
                            error: {
                                status: 503,
                                msg: 'Api deshabilitada',
                            },
                        });
                    } else {
                        // These responses include both the fake 404 from v6 and the status codes 401, 403, 404 and 500 from v7 ('x-stt-req' doesn't exist in the fake 404)
                        response
                            .json()
                            .then((result: any) => {
                                reject({
                                    error: {
                                        status: response.status,
                                        msg:
                                            result.message ||
                                            (result.error && result.error.msg),

                                        errors: result?.error?.errors,

                                        log:
                                            (response.headers.get(
                                                'X-Request-Id',
                                            ) &&
                                                response.headers.get(
                                                    'X-Request-Id',
                                                )) ||
                                            (result.log && result.log.id),
                                    },
                                });
                            })
                            .catch((err: any) => {
                                reject({
                                    error: {
                                        status: response.status,
                                        msg:
                                            err.message ||
                                            (err.error && err.error.msg),
                                    },
                                });
                            });
                    }
                })
                .catch((err: any) => {
                    // Not even a status code

                    reject({
                        error: {
                            msg: 'Error desconocido',
                            log: JSON.stringify(err),
                        },
                    });
                })
                .finally(() => {
                    if (method !== 'GET') {
                        setApiLocks(
                            apiLocks.filter((lock: any) => lock !== apiIndex),
                        );
                    }
                });
        });
    }

    /**
     * Obtiene el path completo de un endpoint (dominio + version + path).
     * @param resource Path a consultar.
     * @returns
     */
    function getResource(resource: string, method: HttpVerb = 'GET') {
        const version = getEPVersion(method, resource);
        return `${apiEnv.url}/${version}${resource}`;
    }

    return [
        request,
        migrationEndpoints,
        setMigrationApis,
        getResource,
    ] as const;
}

/**
 * Hook que devuelve los valores de los parámetros de consulta de la URL actual.
 */
export function useQuery() {
    const { search } = useLocation();

    return getQueryStrings(search, {
        parseNumbers: true,
        parseBooleans: true,
    });
}

/**
 * Get user geolocation HOOK. Credits: https://github.com/trekhleb/use-position
 */
const defaultSettings = {
    enableHighAccuracy: false,
    timeout: Infinity,
    maximumAge: 0,
};

export function usePosition(watch = false, settings = defaultSettings) {
    const [position, setPosition] = useState<{
        latitude?: number;
        longitude?: number;
        accuracy?: number;
        speed?: number | null;
        timestamp?: number;
    }>({});
    const [error, setError] = useState<string | null>(null);

    const onChange = ({ coords, timestamp }: GeolocationPosition) => {
        setPosition({
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
            speed: coords.speed,
            timestamp,
        });
    };

    const onError = (err: GeolocationPositionError) => {
        setError(err.message);
    };

    useEffect(() => {
        if (!navigator || !navigator.geolocation) {
            setError('Geolocation is not supported');
            return;
        }

        let watcher: number | null = null;
        if (watch) {
            watcher = navigator.geolocation.watchPosition(
                onChange,
                onError,
                settings,
            );
        } else {
            navigator.geolocation.getCurrentPosition(
                onChange,
                onError,
                settings,
            );
        }

        return () => {
            if (watcher) {
                navigator.geolocation.clearWatch(watcher);
            }
        };
    }, [settings.enableHighAccuracy, settings.timeout, settings.maximumAge]);

    return { ...position, error };
}

/**
 * useLoadingBar: exposes a loading bar component along with its show and hide functions. For use with a case by case basis.
 * @param bool initialVisibility
 * @returns [function show, function hide, ReactNode SttLoadingBar]
 */
export function useLoadingBar(initialVisibility = false) {
    const [visible, setVisible] = useState(initialVisibility);

    function show() {
        setVisible(true);
    }

    function hide() {
        setVisible(false);
    }

    return [show, hide, <SttLoadingBar visible={visible} />] as const;
}

/**
 * Opens a confirmation dialog with the parameters specified in 'openDialog'.
 * Returns [openingFunction, closingFunction, renderedDialogComponent]
 */
export function useConfirmDialog() {
    const [state, setState] = useState<{
        open: boolean;
        title: string | null;
        content: string | null;
        onConfirm: (() => void) | null;
        onCancel: (() => void) | null;
    }>({
        open: false,
        title: null,
        content: null,
        onConfirm: null,
        onCancel: null,
    });

    function closeDialog() {
        setState({ ...state, open: false });
    }

    function openDialog({
        title = null,
        content = null,
        onConfirm,
        onCancel,
    }: {
        title?: string | null;
        content?: string | null;
        onConfirm?: (() => void) | null;
        onCancel?: (() => void) | null;
    }) {
        setState({
            open: true,
            title,
            content,
            onConfirm: () => {
                if (onConfirm) {
                    onConfirm();
                }
                closeDialog();
            },
            onCancel: () => {
                if (onCancel) {
                    onCancel();
                }
                closeDialog();
            },
        });
    }

    return [
        openDialog,
        closeDialog,
        <DialogComponent
            open={state.open}
            title={state.title ?? undefined}
            content={state.content ?? undefined}
            onCancel={state.onCancel ?? undefined}
            onConfirm={state.onConfirm ?? undefined}
        />,
    ] as const;
}

/**
 * Hook to make a load spinner appear, generally used for API calls (using status value returned by react-query).
 */
export function useLoader(statusList: string[] = []) {
    // Check for loading states (react-query uses 'loading' in v4 and 'pending' in v5)
    const isLoading = statusList.some(
        (status) => status === 'loading' || status === 'pending',
    );

    // Use JSON.stringify for stable comparison of array contents
    const statusKey = statusList.join(',');
    const [loading, setLoading] = useState(isLoading);

    useEffect(() => {
        setLoading(
            statusList.some(
                (status) => status === 'loading' || status === 'pending',
            ),
        );
    }, [statusKey]);

    return [loading, loading ? <SttFullPageSpinner /> : null] as const;
}

/**
 * Hook to set header title.
 */
export function usePageHeader(titleTranslationKey = '') {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;

    function setPageTitle(translationKey: string) {
        cxt.setHeader({ title: t(translationKey) });
    }

    useEffect(() => {
        cxt.setHeader({ title: t(titleTranslationKey) });
    }, [cxt.header?.title, titleTranslationKey]);

    return [setPageTitle] as const;
}

/**
 * Hook for filtering SttCachedTable so that when modifying a filter the request is always made regardless of
 * whether the filter has been modified or not. It was requested because it was necessary to request data when
 * clicking on the filters when it was not changed.
 */
export function useQueryFilters<TFilters extends Record<string, unknown>>(
    filtersValue: TFilters,
    queryKey?: string,
) {
    const queryClient = useQueryClient();
    const [filters, setFiltersValues] = useState(filtersValue);

    /**
     * Update the filters in case they are different, otherwise invalidate the
     * cache so that SttCachedTable is updated.
     * @param updatedFilters New filters value.
     */
    const setFilters = (updatedFilters: TFilters) => {
        if (checkObjEqual(updatedFilters, filters)) {
            if (queryKey) {
                queryClient.invalidateQueries({ queryKey: [queryKey] });
            }
        } else {
            setFiltersValues(updatedFilters);
        }
    };

    return [filters, setFilters] as const;
}
/**
 * Hook for the use of translations.
 */
export function useTranslations() {
    const ctx = useContext(AppContext);

    if (!ctx || !ctx.t) {
        throw new Error(
            'useTranslationState must be used within the AppProvider',
        );
    }

    return { translate: ctx.t };
}

/**
 * Hook for interaction with files. Returns the methods for opening, downloading and uploading files.
 */
export function useInteractionsFiles() {
    const {
        api,
        getResource,
        showMessage,
        t: translate,
    } = useContext(AppContext)!;

    /**
     * Requests a file using the API.
     */
    const requestFile = (
        url: string,

        type = 'text/plain',
        params: Record<string, unknown> | null | undefined,
        method = 'GET',
        bodyParams: Record<string, unknown> | null = null,
    ) =>
        new Promise<string>((resolve: any, reject: any) => {
            const req = new XMLHttpRequest();

            // api migrations version (new)
            const serializedParams = params
                ? Object.keys(params).reduce(
                      (res, key, i) =>
                          `${res}${i === 0 ? '?' : '&'}${key}=${params[key]}`,
                      '',
                  )
                : '';

            let fileUrl = url + serializedParams;

            if (fileUrl.indexOf('https') === -1) {
                fileUrl = getResource(fileUrl, method as HttpVerb);
            }

            req.open(method, fileUrl, true);
            req.withCredentials = true;

            req.onreadystatechange = function handleStateChange() {
                if (req.readyState === 4) {
                    if (req.status !== 200 && req.responseText !== '') {
                        if (req.status === 404 || req.status === 400) {
                            showMessage(
                                'E',
                                getErrorMessage(
                                    JSON.parse(req.response as string),
                                ),
                            );
                        }
                        reject(JSON.parse(req.response as string));
                    }
                } else if (req.readyState === 2) {
                    if (req.status === 200) {
                        req.responseType = 'blob';
                    } else {
                        req.responseType = 'text';
                    }
                }
            };

            req.onload = function handleLoad() {
                // En v6, los CSV se envían en Windows-1252, mientras que en v7 en UTF-8.
                // Para que Excel interprete los CSV de v7 correctamente, se agrega "charset" al type.
                let blobType = type;
                if (type === 'text/csv' && fileUrl.includes('/v7/')) {
                    blobType = 'text/csv;charset=utf-8';
                }

                const blob = new Blob([req.response], { type: blobType });
                const data = window.URL.createObjectURL(blob);

                // This data url is our result
                resolve(data);

                // revoke the object url after a short delay
                setTimeout(() => {
                    window.URL.revokeObjectURL(data);
                }, 100);
            };

            req.onerror = function handleError() {
                reject(req.response);
            };

            req.ontimeout = function handleTimeout() {
                reject('Request timeout');
            };

            if (method === 'POST' && bodyParams) {
                req.setRequestHeader('Content-Type', 'application/json');
                const body = JSON.stringify(bodyParams);
                req.send(body);
            } else {
                req.send();
            }
        });

    /**
     * Open file invoked by API. Similar to downloadFile, except this one opens the file directly in the browser
     * @param url Request URL.
     * @param type File type.
     * @param params Object with params.
     * @param downloadFilename Name of the file to be downloaded.
     */
    const openFile = (
        url: string,

        type = 'text/plain',
        params: Record<string, unknown> | null = null,
        downloadFilename = translate('DownloadNoun'),
        method = 'GET',
        bodyParams = null,
    ) => {
        requestFile(url, type, params, method, bodyParams).then((dataUrl) => {
            // Create link with target '_new' and click it
            const link = document.createElement('a');
            link.href = dataUrl;
            link.target = '_new';

            // On Chromium (Chrome, Edge) the browser cannot download a file opened via a blob url, therefore, hack:
            if (window.navigator.userAgent.includes('Chrome')) {
                link.download = downloadFilename; // fall backs to 'Download.<extension>'
            }

            link.click();
        });
    };

    /**
     * Download file invoked by API. Opens a possibly authorized request to a file and then opens it as
     * if the user had clicked a direct link.
     * @param url Relative url of the file to download, for example: /scs/1245/payments.pdf.
     * @param type Mime type of the file, for example 'application/pdf'.
     * @param params Like unknown other API call, {key1: values1, key2: value2}.
     * @param downloadFilename Name of the file to be downloaded.
     * @param method Method to execute the request.
     * @param bodyParams If method is POST, the body of the request
     * @param onError If exists, onError callback
     * @param onSuccess If exists, onSuccess callback
     */
    const downloadFile = (
        url: string,

        type = 'text/plain',
        params: Record<string, unknown> | undefined,
        downloadFilename = translate('DownloadNoun'),
        method = 'GET',
        bodyParams: Record<string, unknown> | null = null,
        onError?: (response: unknown) => void,
        onSuccess?: () => void,
    ) => {
        requestFile(url, type, params, method, bodyParams)
            .then((dataUrl) => {
                // Create a "download link" and click it
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = downloadFilename;
                link.click();

                if (onSuccess) {
                    onSuccess();
                }
            })

            .catch((error: string) => {
                if (onError) onError(error);
                else showMessage('E', getErrorMessage(error));
            });
    };

    /**
     * Subir fichero usando content-type: multipart/form-data
     * @param metaData Object with values. For USER_PHOTO: {name, type, idMship}. For others: {value, type, format}
     * @param file The file like from an <input type="file">
     * @returns {Promise<unknown>}
     */
    const uploadFile = (
        metaData: Record<string, string | number | undefined>,
        file: string | Blob,
    ) => {
        const { apiEnv } = window.runtimeConfig;

        return new Promise((resolve: any, reject: any) => {
            const formData = new FormData();

            Object.entries(metaData).forEach(([key, value]) => {
                if (value !== undefined) {
                    formData.append(key, String(value));
                }
            });

            formData.append('file', file);

            // No podemos usar api() porque no soporta headers custom (por dentro está el content-type hardcodeado etc)
            axios({
                url: `${apiEnv.url}/v7/uploads`, // solo existe en v7
                method: 'POST',
                data: formData,
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true,
            })
                .then((response: { data: unknown }) => response.data)
                .then((response: any) => {
                    if (isObject(response) && 'error' in response) {
                        reject(response.error);
                    } else {
                        resolve(response); // esto debe ser {file}
                    }
                })
                .catch((error: any) => {
                    // 869df3wdd: reject with the backend response body
                    // (e.g. { error: { msg } } from a 400) so the caller can
                    // surface a descriptive message instead of a generic one.
                    console.error(error);
                    reject(error?.response?.data ?? error);
                });
        });
    };

    /**
     * Subir fichero usando la api de subida de V6 en dos pasos con application/x-www-form-urlencoded.
     * @param metaData
     * @param fileContent
     * @returns {Promise<unknown>}
     */
    const uploadFileLegacyV6 = (
        metaData: Record<string, unknown>,
        fileContent: unknown,
    ): Promise<unknown> =>
        new Promise((resolve: any, reject: any) => {
            api('POST', '/files', {
                params: metaData,
                success: (data: any) => {
                    if (isObject(data) && data.file) {
                        // After creating file, upload the content
                        axios({
                            method: 'POST',
                            url: `${data.fileRowUploadingUrl}`,
                            params: null,
                            data: fileContent,
                            headers: {
                                'Content-Type':
                                    'application/x-www-form-urlencoded',
                            },
                            withCredentials: true,
                        })
                            .then((response: any) => {
                                if (isErrorResponse(response)) {
                                    reject(response.error);
                                } else {
                                    resolve({ file: data.file, response });
                                }
                            })
                            .catch((error: any) => {
                                showMessage(
                                    'E',
                                    'Unknown error on Sporttia Server',
                                );

                                console.error(error);
                                reject(error);
                            });
                    } else {
                        reject('Error: no file in resp :(');
                    }
                },
            });
        });

    return { openFile, downloadFile, uploadFile, uploadFileLegacyV6 } as const;
}

/**
 * Hook return a countdown timer.
 */
export function useCountDownTimer({
    initialSeconds = 60,
    pause = false,
    onTimeOut = null,
}: {
    initialSeconds?: number;
    pause?: boolean;
    onTimeOut: (() => void) | null;
}) {
    const [seconds, setSeconds] = useState(initialSeconds);
    const [formatTime, setFormatTime] = useState(
        secondsToFormatTime(initialSeconds),
    );
    const [timeOut, setTimeOut] = useState(false);
    const [pauseTimer, setPauseTimer] = useState(pause);
    const toggleTimer = () => setPauseTimer((t) => !t);

    useEffect(() => {
        if (seconds === 0) {
            setTimeOut(true);
            if (onTimeOut) {
                onTimeOut();
            }
        } else if (!pauseTimer) {
            const timeout = setTimeout(() => {
                const updatedSeconds = seconds - 1;
                setSeconds(updatedSeconds);
                setFormatTime(secondsToFormatTime(updatedSeconds));
            }, 1000);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [seconds, pauseTimer]);

    return { formatTime, timeOut, toggleTimer } as const;
}

/**
 * Return APP Environment.
 * @deprecated Utilizar config/constants
 */
export function useEnvironment() {
    let environment = 'PRO';
    if (window.runtimeConfig.apiEnv.environment === 'PRE') {
        environment = 'PRE';
    } else if (
        window.runtimeConfig.apiEnv.environment === 'DEV' ||
        process.env.NODE_ENV === 'development'
    ) {
        environment = 'DEV';
    }

    return environment;
}

/**
 * Hook to check if element is in the viewport.
 */
export function useOnViewport(
    ref: MutableRefObject<HTMLElement>,

    rootMargin = '0px',
    onViewport: () => void,
) {
    // State and setter for storing whether element is visible
    const [isIntersecting, setIntersecting] = useState<boolean>(false);

    useEffect(() => {
        const element = ref.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Update our state when observer callback fires
                setIntersecting(!!entry && entry.isIntersecting);

                if (entry && entry.isIntersecting && onViewport) {
                    onViewport();
                }
            },
            {
                rootMargin,
            },
        );

        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, []); // Empty array ensures that effect is only run on mount and unmount

    return isIntersecting;
}

// Backoff exponencial (ms) para reconexión del WebSocket de notificaciones de acceso
const SOCKET_RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000];

// Heartbeat aplicativo: enviamos {type:'ping'} cada 25s, el server responde
// {type:'pong'}. El watchdog detecta silencio prolongado del server y fuerza
// reconexión (cubre Modo B: TCP half-open donde onclose no se dispara).
// 25s mantiene la conexión bajo cualquier proxy razonable y es coherente con
// el heartbeat SSE del backend. En tabs en background el throttle del
// navegador lo degrada a ~60s, suficiente para evitar idle del proxy.
const SOCKET_KEEPALIVE_INTERVAL_MS = 25_000;

// Silencio del servidor que se tolera antes de marcar la conexión como zombie.
// 2.5x el intervalo de ping da margen para una pérdida puntual sin disparar
// reconexiones espurias; la regla two-strike del watchdog añade un segundo
// margen (worst-case ~87s para forzar la reconexión).
const SOCKET_PONG_TIMEOUT_MS = SOCKET_KEEPALIVE_INTERVAL_MS * 2.5;

// Ventana de gracia para recibir el primer pong tras el handshake. Si el
// backend no responde pongs (despliegue parcial frontend↔backend), el
// watchdog se auto-desactiva para no entrar en un bucle de reconexión.
const SOCKET_HANDSHAKE_TIMEOUT_MS = 30_000;

// Cap defensivo de reconexiones consecutivas fallidas. ~20 intentos con el
// backoff exponencial actual suman ~10 min antes de rendirse. Evita un
// runaway loop si el backend está caído de forma permanente o la URL del
// socket no es resoluble.
const MAX_CONSECUTIVE_FAILS = 20;

/**
 * Hook para establecer una conexión mediante websocket contra el servidor de v7
 * Actualmente no es un procedimiento genérico para abrir sockets, sino que tiene funcionalidades muy específicas para
 * el sistema de notificaciones de accesos de usuarios a través de los dispositivos físicos (tornos y demás)
 */
export function useSocketConnection() {
    useContext(AppContext); // No se utilizaba el objeto ctx. No se a removido la llamada a la espera de comprobar si es necesario.
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [ready, setReady] = useState(false);
    const [receivedMessages, setReceivedMessages] = useState<SocketMessage[]>(
        [],
    );
    const [queuedDeletions, setQueuedDeletions] = useState<SocketMessage[]>([]);

    const reconnectAttemptRef = useRef(0);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const keepaliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(
        null,
    );
    const shouldReconnectRef = useRef(false);
    // Evita que visibilitychange + online (o cualquier doble disparo) abran dos WebSocket en paralelo.
    const isOpeningRef = useRef(false);

    // Estado del watchdog de pong. lastPongAt = 0 actúa como sentinel "ningún
    // pong recibido"; se actualiza en onmessage al recibir {type:'pong'}.
    const watchdogStateRef = useRef<WatchdogState>({
        lastPongAt: 0,
        strikes: 0,
    });
    const watchdogTimerRef = useRef<ReturnType<typeof setInterval> | null>(
        null,
    );
    const handshakeProbeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const connectionStartedAtRef = useRef<number>(0);
    const consecutiveFailsRef = useRef<number>(0);

    // calc a pseudo id for the message as <access type>:<mship id>
    const pseudoId = (message: SocketMessage) =>
        message ? `${message?.access?.type}:${message?.mship?.id}` : '';

    // eliminar un mensaje de los recibidos. Se identifican por tipo + mship.id
    const deleteMessage = (message: SocketMessage) => {
        const id = pseudoId(message);

        const found = receivedMessages.find((msg: any) => pseudoId(msg) === id);

        if (found) {
            const newMessages = receivedMessages.filter(
                (msg: any) => pseudoId(msg) !== id,
            );
            setReceivedMessages(newMessages);
        }
    };

    const closeSocket = (message = 'socket closed') => {
        shouldReconnectRef.current = false;
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        if (keepaliveTimerRef.current) {
            clearInterval(keepaliveTimerRef.current);
            keepaliveTimerRef.current = null;
        }
        if (watchdogTimerRef.current) {
            clearInterval(watchdogTimerRef.current);
            watchdogTimerRef.current = null;
        }
        if (handshakeProbeTimerRef.current) {
            clearTimeout(handshakeProbeTimerRef.current);
            handshakeProbeTimerRef.current = null;
        }
        if (socket) {
            socket.close(1000, message);
        }
    };

    useEffect(() => {
        if (socket) {
            setReady(true);
        }
    }, [socket]);

    // Mensajes pendiente de borrado. Estoy hay que hacerlo así porque no interactúan bien los estados de react con
    // las funciones declaradas como callbacks de eventos (un absoluto sindiós, si valoras tu salud mental, huye).
    useEffect(() => {
        if (queuedDeletions.length > 0) {
            queuedDeletions.forEach(deleteMessage);
            setQueuedDeletions([]);
        }
    }, [queuedDeletions.length]);

    // inserta un mensaje en la cola de borrado
    const queueMessageDeletion = (message: SocketMessage) => {
        setQueuedDeletions([...queuedDeletions, message]);
    };

    // enviar mensaje al socket
    const sendMessage = (message: SocketMessage) => {
        if (socket) {
            socket.send(JSON.stringify(message));
        }
    };

    /**
     * Por algún extraño bug de React, la interacción entre socket.onmessage y los estados de react no funciona bien.
     * El callback asignado a socket.onmessage no es capaz de leer los cambios de estado en el componente.
     * Como solución asignamos el callback de nuevo cada vez que llegue un mensaje, para que desde la func. puedan obtenerse
     * correctamente el número de mensajes previos.
     * Nota: si crees que esto iría mejor dentro de openSocket(), estás equivocado. No pierdas el tiempo intentándolo.
     */
    useEffect(() => {
        if (ready && socket) {
            socket.onmessage = (event: { data: string }) => {
                let message: SocketMessage & { type?: string };
                try {
                    message = JSON.parse(event.data) as SocketMessage & {
                        type?: string;
                    };
                } catch (err) {
                    console.error(
                        `[onmessage] failed to parse message: ${getErrorMessage(err)}`,
                    );
                    return;
                }

                // Heartbeat aplicativo: el servidor responde {type:'pong'} a
                // nuestros pings. El watchdog detecta silencio prolongado
                // (Modo B: TCP half-open donde onclose no se dispara).
                if (message?.type === 'pong') {
                    watchdogStateRef.current = {
                        lastPongAt: Date.now(),
                        strikes: 0,
                    };
                    return;
                }

                // Ignorar mensajes que no sean notificaciones de acceso (p.ej. eco de pings)
                if (!message?.access && !message?.mship) {
                    return;
                }

                const messages = [...receivedMessages, message];

                // Añadir a la cola de mensajes por borrar tras 10 secs.
                setTimeout(() => {
                    queueMessageDeletion(message);
                }, 10000);

                setReceivedMessages(messages);
            };
        }
    }, [receivedMessages, ready, socket]);

    // Reabre el socket cuando el usuario vuelve a la pestaña o recupera red, si está caído.
    // Cubre el caso de TCP zombie tras suspender el equipo o cambiar de red.
    useEffect(() => {
        const reconnectIfStale = () => {
            if (
                shouldReconnectRef.current &&
                (!socket || socket.readyState !== WebSocket.OPEN)
            ) {
                if (reconnectTimerRef.current) {
                    clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = null;
                }
                openSocket();
            }
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') reconnectIfStale();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('online', reconnectIfStale);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('online', reconnectIfStale);
        };
        // openSocket se redefine cada render pero solo lee refs/setters; capturar la versión vigente cuando cambia socket es suficiente.
    }, [socket]);

    /**
     * Func. principal para establecimiento de la comunicación.
     * socket.onmessage no obstante está declarada dentro de un efecto secundario que depende de receivedMessages (ver más arriba)
     */
    const openSocket = () => {
        if (isOpeningRef.current) {
            return;
        }
        try {
            const socketConf = window.runtimeConfig.socket;

            if (!socketConf) {
                throw new Error(
                    'Falta window.runtimeConf.socket. Añade "socket: {url: url_de_conexion}" ',
                );
            }

            shouldReconnectRef.current = true;
            isOpeningRef.current = true;

            const newSocket = new WebSocket(socketConf.url);

            console.log(`Opening socket connection to ${socketConf.url}`);

            // Initial message with device registration
            newSocket.onopen = () => {
                isOpeningRef.current = false;
                reconnectAttemptRef.current = 0;
                consecutiveFailsRef.current = 0;
                newSocket.send(
                    JSON.stringify({
                        data: {
                            deviceIds: localStorage.getItem('registeredDevices')
                                ? (JSON.parse(
                                      localStorage.getItem(
                                          'registeredDevices',
                                      )!,
                                  ) as unknown)
                                : [],
                        },
                    }),
                );

                // Keep-alive aplicativo. Genera tráfico TCP periódico para que el proxy
                // no corte la conexión por idle. Si el send falla, onclose se disparará
                // y la lógica de reconexión se encargará.
                if (keepaliveTimerRef.current) {
                    clearInterval(keepaliveTimerRef.current);
                }
                keepaliveTimerRef.current = setInterval(() => {
                    if (newSocket.readyState === WebSocket.OPEN) {
                        try {
                            newSocket.send(JSON.stringify({ type: 'ping' }));
                        } catch (err) {
                            console.error(
                                `[keepalive] send failed: ${getErrorMessage(err)}`,
                            );
                        }
                    }
                }, SOCKET_KEEPALIVE_INTERVAL_MS);

                // Watchdog del heartbeat aplicativo. Cubre el Modo B (TCP
                // half-open por NAT/firewall intermedio): si dejamos de
                // recibir pongs durante demasiado tiempo y onclose no se
                // dispara, forzamos la reconexión manualmente. La regla
                // two-strike de watchdogTick tolera un fallo aislado.
                // Inicializamos lastPongAt al mismo timestamp que
                // connectionStartedAt. Si llega un pong real, lastPongAt avanza
                // por encima y shouldDisableAfterHandshake devuelve false.
                // Si no llega, queda igual y dispara el auto-disable.
                connectionStartedAtRef.current = Date.now();
                watchdogStateRef.current = {
                    lastPongAt: connectionStartedAtRef.current,
                    strikes: 0,
                };
                if (watchdogTimerRef.current) {
                    clearInterval(watchdogTimerRef.current);
                }
                watchdogTimerRef.current = setInterval(() => {
                    const tick = watchdogTick(
                        Date.now(),
                        watchdogStateRef.current,
                        SOCKET_PONG_TIMEOUT_MS,
                    );
                    watchdogStateRef.current = tick.state;
                    if (
                        tick.forceReconnect &&
                        newSocket.readyState === WebSocket.OPEN
                    ) {
                        const silenceMs = Date.now() - tick.state.lastPongAt;
                        console.log(
                            `[watchdog] no pong in ${silenceMs}ms, forcing reconnect`,
                        );
                        newSocket.close(1000, 'pong timeout');
                    }
                }, SOCKET_KEEPALIVE_INTERVAL_MS);

                // Graceful degradation: si el backend no responde pongs
                // (despliegue parcial frontend↔backend), desactivamos el
                // watchdog para esta sesión en lugar de entrar en un bucle
                // de reconexión cada ~87s.
                if (handshakeProbeTimerRef.current) {
                    clearTimeout(handshakeProbeTimerRef.current);
                }
                handshakeProbeTimerRef.current = setTimeout(() => {
                    const disable = shouldDisableAfterHandshake(
                        Date.now(),
                        connectionStartedAtRef.current,
                        watchdogStateRef.current.lastPongAt,
                        SOCKET_HANDSHAKE_TIMEOUT_MS,
                    );
                    if (disable && watchdogTimerRef.current) {
                        console.log(
                            '[watchdog] server does not respond to pings, disabling watchdog for this session',
                        );
                        clearInterval(watchdogTimerRef.current);
                        watchdogTimerRef.current = null;
                    }
                }, SOCKET_HANDSHAKE_TIMEOUT_MS);
            };

            // Socket closed
            newSocket.onclose = function handleOnClose(event: any) {
                isOpeningRef.current = false;
                if (event.wasClean) {
                    console.log(
                        `[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`,
                    );
                } else {
                    // e.g. server process killed or network down
                    // event.code is usually 1006 in this case
                    console.log(
                        `[close] Connection died, code=${event.code} reason=${event.reason}`,
                    );
                }

                if (keepaliveTimerRef.current) {
                    clearInterval(keepaliveTimerRef.current);
                    keepaliveTimerRef.current = null;
                }
                if (watchdogTimerRef.current) {
                    clearInterval(watchdogTimerRef.current);
                    watchdogTimerRef.current = null;
                }

                // Reconectar siempre que el cierre no lo hayamos pedido nosotros (closeSocket pone shouldReconnectRef=false).
                // Cubre cierres limpios por idle del proxy (code 1000/1001) además de los caídos (1006).
                // Excepción: si el server cierra por sesión inválida (code 1000 + reason '401: Unauthorized')
                // no reconectamos para evitar un bucle perpetuo contra el endpoint de auth.
                const isAuthFailure =
                    event.code === 1000 &&
                    typeof event.reason === 'string' &&
                    /unauthor|401/i.test(event.reason);

                if (shouldReconnectRef.current && !isAuthFailure) {
                    consecutiveFailsRef.current += 1;
                    if (consecutiveFailsRef.current > MAX_CONSECUTIVE_FAILS) {
                        console.log(
                            `[reconnect] giving up after ${consecutiveFailsRef.current} consecutive failures`,
                        );
                        shouldReconnectRef.current = false;
                        return;
                    }
                    const idx = Math.min(
                        reconnectAttemptRef.current,
                        SOCKET_RECONNECT_DELAYS_MS.length - 1,
                    );
                    const delay =
                        (SOCKET_RECONNECT_DELAYS_MS[idx] ?? 30000) +
                        Math.random() * 1000;
                    reconnectAttemptRef.current += 1;
                    console.log(
                        `[reconnect] scheduling attempt ${reconnectAttemptRef.current} in ${Math.round(delay)} ms`,
                    );
                    reconnectTimerRef.current = setTimeout(() => {
                        if (shouldReconnectRef.current) {
                            openSocket();
                        }
                    }, delay);
                }
            };

            // Socket error
            newSocket.onerror = function handleOnError(error: any) {
                console.error(`[error] ${getErrorMessage(error)}`);
                // El reconnect lo gestiona onclose, que se dispara después de onerror
            };

            setSocket(newSocket);
        } catch (error) {
            isOpeningRef.current = false;
            console.error(error);
        }
    };

    return [
        openSocket,
        closeSocket,
        receivedMessages,
        sendMessage,
        deleteMessage,
    ] as const;
}

/**
 * @deprecated Utilizar DateBuilder
 * Mover a utils. No es necesario que sea un custom hook
 * Hook para eliminar partes de una fecha para casos donde solo se quiera mostrar bajo un formato concreto
 */
export function useFormatDate() {
    const removeSeconds = (date: string) => {
        if (date.split(':').length === 3) {
            return date.slice(0, date.lastIndexOf(':'));
        }
        return date;
    };

    return [removeSeconds] as const;
}

/**
 * Hook para evitar bug en SttCachedTable donde la gestion de estado de los filtros cambia innecesariamente
 */
export function usePrevious(value: unknown) {
    const ref = useRef<unknown>(undefined);

    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
}

/**
 * Devuelve una función de configuración que, según POSType (PAYCOMET, etc) permite inicializar la config pasandole la ip local del datáfono
 */
export function usePOSConfig(POSType: unknown) {
    const environment = useEnvironment();

    const setupConfig = (ip: unknown) => {
        let uri;

        let config;

        switch (POSType) {
            case 'PAYCOMET':
            default:
                uri = `https://${ip}:${environment === 'DEV' ? 3001 : 3011}`;
                // Configuración de endpoints
                config = {
                    init: {
                        method: 'POST',
                        uri: `${uri}/v1/transactions/init`,
                        params: () => {},
                        response: (input: {
                            resultCode: unknown;
                            resultMessage: unknown;
                        }) => ({
                            // eslint-disable-next-line eqeqeq
                            status: input.resultCode == 1000 ? 'OK' : 'ERROR',
                            message: input.resultMessage,
                        }),
                    },
                    transaction: {
                        method: 'POST',
                        uri: `${uri}/v1/transactions/payment`,
                        params: (input: {
                            amount: unknown;
                            idTpv: unknown;
                            concept: unknown;
                        }) => ({
                            amount: input.amount,
                            orderId: input.idTpv,
                            description: input.concept,
                        }),
                        response: (input: {
                            resultCode: unknown;
                            ticket: { Status: unknown };
                            resultMessage: unknown;
                        }) => ({
                            status:
                                // eslint-disable-next-line eqeqeq
                                input.resultCode == 0 &&
                                input.ticket &&
                                // eslint-disable-next-line eqeqeq
                                input.ticket?.Status == '0'
                                    ? 'OK'
                                    : 'ERROR',

                            message: input.resultMessage,
                            ticket: input.ticket,
                        }),
                    },
                    refund: {
                        method: 'POST',
                        uri: `${uri}/v1/transactions/refund`,
                        params: (input: {
                            amount: unknown;
                            idTpv: unknown;
                            concept: unknown;
                            idPaycomet: unknown;
                        }) => ({
                            amount: input.amount,
                            transactionId: input.idPaycomet,
                            orderId: input.idTpv,
                            description: input.concept,
                        }),
                        response: (input: {
                            resultCode: unknown;
                            ticket: { Status: unknown };
                            resultMessage: unknown;
                        }) => ({
                            status:
                                // eslint-disable-next-line eqeqeq
                                input.resultCode == 0 &&
                                input.ticket &&
                                // eslint-disable-next-line eqeqeq
                                input.ticket?.Status == 0
                                    ? 'OK'
                                    : 'ERROR',

                            message: input.resultMessage,
                            ticket: input.ticket,
                        }),
                    },
                    printLastTransaction: {
                        method: 'GET',
                        uri: `${uri}/v1/transactions/last/print`,
                        params: () => {},
                        response: () => null,
                    },
                };
                break;
        }

        return config;
    };

    return setupConfig;
}

type UseIntervalParams = {
    isActive?: boolean;
    delayMs: number;
};

/**
 * Custom hook for creating an interval that invokes a callback function.
 */
export const useInterval = (
    callback: () => void,
    options: UseIntervalParams,
) => {
    const savedCallback = useRef<() => void>(undefined);

    const isActive = options.isActive ?? true;

    useEffect(() => {
        savedCallback.current = callback;
    });

    useEffect(() => {
        if (!isActive) {
            return;
        }

        const id = setInterval(() => {
            savedCallback.current?.();
        }, options.delayMs);

        return () => {
            clearInterval(id);
        };
    }, [isActive, options.delayMs]);
};

export const useGetGameLink = () => {
    const { translate } = useTranslations();

    const getGameLink = (game?: Game) => {
        if (!game) {
            return { link: '', parsedLink: '' };
        }

        const dateIni = new DateBuilder(game.ini);
        const dateEnd = new DateBuilder(game.end);

        const link = `${window.runtimeConfig.sharing.url}/games/${game.id}${
            game.signupCode ? `?code=${game.signupCode}` : ''
        }`;

        const parsedLink = `
        *${translate('gameInvitation')}* 🏟️\n\n

        *📍 ${translate('Center')}:* ${game.facility?.sc?.name}\n
        *📅 ${translate('Date')}:* ${dateIni.cdlhm()}\n
        *⏰ ${translate('Hour')}:* ${dateIni.dmyhm()} - ${dateEnd.dmyhm()}\n
        *🎯 ${translate('Level')}:* ${game.level}\n

        ${translate('joinGameHere')}:\n  ${link}`;

        return { link, parsedLink };
    };

    return { getGameLink };
};

export function useShareGame(game?: Game) {
    const { link, parsedLink } = useGetGameLink().getGameLink(game);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [copyLoading, setCopyLoading] = useState(false);

    const handleCopyShareText = () => {
        setCopyLoading(true);
        navigator.clipboard.writeText(link);
        setSnackbarOpen(true);
        setCopyLoading(false);
    };

    const handleShareWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(parsedLink)}`;
        window.open(url, '_blank');
    };

    const handleShareTwitter = () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(parsedLink)}`;
        window.open(url, '_blank');
    };

    return {
        link,
        parsedLink,
        snackbarOpen,
        setSnackbarOpen,
        copyLoading,
        handleCopyShareText,
        handleShareWhatsApp,
        handleShareTwitter,
    };
}
